/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS } from '../commonStateTaxData';
import {
  step1InitialQuestions, maritalStatusQuestion,
  additionalWithholdingQn1223, exemptionInitialQuestions,
  civilReliefActStateQuestion, worksheetDeductionQuestions1223,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version1223({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  lineHDivisor = 4000,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // Q1AB, Q2AB:
  const basicInfoForm = useRef();
  const step1InitQ = getInitialQuestions(step1InitialQuestions);
  const [basicInfo, setBasicInfo] = useState(step1InitQ);

  // Q3-1:
  const maritalStatusForm = useRef();
  const maritalStatusInitQ = getInitialQuestions(maritalStatusQuestion);
  const [maritalStatus, setMaritalStatus] = useState(maritalStatusInitQ);

  // Q4:
  const [numDependentAllowances, setNumDependentAllowances] = useState(data.number_of_dependent_allowances || '');

  // For them to to fill in Georgia Adjustments Allowance in Line 5
  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const worksheetForm = useRef();

  // Q5: Georgia Adjustments Allowance
  const worksheetCalculatorQ = getInitialQuestions(worksheetDeductionQuestions1223);
  const [adjustmentAllowance, setAdjustmentAllowance] = useState(data.adjustment_allowance || '');
  const [worksheetQuestions, setWorksheetQuestions] = useState(worksheetCalculatorQ);
  const tabulatedAdjustments = () => {
    let errors = null;

    worksheetForm.current.validate((err) => {
      if (err) errors = err;
    });

    if (errors) {
      setAdjustmentAllowance('');
      return;
    }

    // initialised all step 5 variables to be 0
    let step5lineA = 0; // Taken from itemized_deduction_value
    let step5lineB = 0;
    let step5lineC = 0;
    let step5lineD = 0;
    let step5lineE = 0;
    let step5lineF = 0;
    let step5lineG = 0;
    let step5lineH = 0;

    const GEORGIA_DEDUCTION_MAPPING = {
      single_head_of_household: 12000,
      married_filing_joint: 24000,
      married_filing_separate: 12000,
    };

    worksheetQuestions.forEach((question) => {
      switch (question.id) {
        case 'itemized_deduction_value':
          step5lineA = Number(question.answer.answer.itemized_deduction_value);
          break;
        case 'georgia_standard_deduction':
          step5lineB = GEORGIA_DEDUCTION_MAPPING[question.answer.answer.georgia_standard_deduction];
          break;
        case 'allowable_deductions_to_federal_adjusted_gross_income':
          step5lineD = Number(question.answer.answer.allowable_deductions_to_federal_adjusted_gross_income);
          break;
        case 'estimate_of_taxable_income':
          step5lineF = Number(question.answer.answer.estimate_of_taxable_income);
          break;
        default:
          break;
      }
    });

    // Start to tabulate derived line (step5LineC, step5LineE, step5LineG, step5LineH)
    step5lineC = Math.max(step5lineA - step5lineB, 0); // If zero or less, enter zero
    step5lineE = parseInt(step5lineC, 10) + parseInt(step5lineD, 10);
    step5lineG = step5lineE - step5lineF;

    // If line G is 0 or less, stop here
    if (step5lineG <= 0) {
      step5lineH = 0;
    } else {
      const lineH = step5lineG / lineHDivisor;

      const lineHRemainder = step5lineG % lineHDivisor;

      step5lineH = lineHRemainder > 1500 ? Math.ceil(lineH) : Math.round(lineH);
    }

    setAdjustmentAllowance(step5lineH.toString()); // Line 5H / Georgia Adjustments Allowances
  };

  useEffect(() => {
    // Each time it detects a change in the worksheetQuestions, it will recalculate the adjustmentAllowance
    // Provided that the worksheet is open
    if (worksheetOpen) {
      tabulatedAdjustments();
    }
  }, [worksheetQuestions]);

  // Q6 - Additional Withholding
  const additionalWitholdingForm = useRef();
  const additionalWithholdingInitQ = getInitialQuestions(additionalWithholdingQn1223);
  const [additionalWithholding, setadditionalWithholding] = useState(additionalWithholdingInitQ);

  // Q8:
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || '');
  const exemptionsForm = useRef();
  const exemptionInitQ = getInitialQuestions(exemptionInitialQuestions);
  const [claimExemption, setClaimExemption] = useState(exemptionInitQ);
  const getExemptionSpecificQuestions = (questions) => {
    if (questions[0].answer.answer.exemption_from_withholding === 'civil_relief_act' || data.exemption_from_withholding === 'civil_relief_act') {
      const exemptionSpecificiInitQ = getInitialQuestions(civilReliefActStateQuestion);
      return exemptionSpecificiInitQ;
    }
  };
  const [exemptionSpecificQuestions, setExemptionSpecificQuestions] = useState(data.exemption_from_withholding ? getExemptionSpecificQuestions(claimExemption) : []);
  const [exemptionSpecificQuestionsError, setExemptionSpecificQuestionsError] = useState(false);

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
    </div>
  );

  const renderMaritalStatusForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={maritalStatus}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = maritalStatus.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setMaritalStatus(questions);
        }}
        ref={maritalStatusForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding. (Line 8)
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderWorksheetContent = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={worksheetQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = worksheetQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWorksheetQuestions(questions);
        }}
        ref={worksheetForm}
      />
    </Flex>
  );

  const renderWorksheet = (isReviewStep) => (
    <Flex column>
      <Flex column>
        <Flex mb="7px">
          <S.QuestionLabel>
            Dependent allowances (Line 4)
          </S.QuestionLabel>
        </Flex>
        <S.QuestionWrapper>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={numDependentAllowances}
              onChange={(e) => setNumDependentAllowances(e.target.value)}
              disabled={isReviewStep}
            />
          </Flex>
        </S.QuestionWrapper>
      </Flex>
      <Flex column>
        <Flex mb="7px">
          <S.QuestionLabel>
            Georgia Adjustments Allowances (Line 5)
          </S.QuestionLabel>
        </Flex>
        <S.QuestionWrapper>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={worksheetOpen ? adjustmentAllowance : 0}
              disabled
            />
          </Flex>
        </S.QuestionWrapper>
      </Flex>
      <Flex mt="10px">
        <CollapsibleQuestions
          title="To claim Georgia Adjustments Allowance, you must complete the worksheet to calculate an amount."
          handleClick={() => {
            if (!isReviewStep) {
              setWorksheetOpen(!worksheetOpen);
            }
          }}
          open={worksheetOpen || isReviewStep}
        >
          {renderWorksheetContent(isReviewStep)}
        </CollapsibleQuestions>
      </Flex>
    </Flex>
  );

  const renderadditionalWitholdingForm = (isReviewStep) => (
    <Flex>
      <FormConsumer
        questions={additionalWithholding}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalWithholding.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setadditionalWithholding(questions);
        }}
        ref={additionalWitholdingForm}
      />
    </Flex>
  );

  const renderExemptionSpecificQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={exemptionSpecificQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionSpecificQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionSpecificQuestions(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const renderExemptionsForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={claimExemption}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = claimExemption.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setClaimExemption(questions);
          setExemptionSpecificQuestions(getExemptionSpecificQuestions(questions));
        }}
        ref={exemptionsForm}
      />
      { exemptionSpecificQuestionsError && isExemptFromWithholding && (
        <Flex justifyContent="flex-start">
          <Text color="red">Your state of residence and your spouse's must be the same to be exempt.</Text>
        </Flex>
      )}
      { claimExemption[0].answer.answer.exemption_from_withholding === 'civil_relief_act' ? renderExemptionSpecificQuestions(isReviewStep) : ''}
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {renderMaritalStatusForm()}
          {isExemptFromWithholding && renderExemptionsForm()}
          {!isExemptFromWithholding && renderWorksheet()}
          {!isExemptFromWithholding && renderadditionalWitholdingForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;

            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            // Basic Info Questions //
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            // Marital Status Questions (Line 3) //
            if (maritalStatusForm && maritalStatusForm.current) {
              maritalStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            // Exemptions Questions (Line 8) //
            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }

            // Exemption Specific Questions (Line 8) //
            if (isExemptFromWithholding) {
              if (claimExemption[0].answer.answer.exemption_from_withholding === 'civil_relief_act') {
                if (exemptionSpecificQuestions[0].answer.answer.state_of_residence !== '' && (basicInfo[4].answer.answer.state !== exemptionSpecificQuestions[0].answer.answer.state_of_residence)) {
                  err = true;
                  setExemptionSpecificQuestionsError(true);
                } else {
                  setExemptionSpecificQuestionsError(false);
                }
              }
            }
            if (err) {

            } else {
              // No error, proceed to save to the database //

              // Basic information
              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              // Marital status
              const maritalStatusData = maritalStatus
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              // Reset Line 5
              const worksheetResetDeductionData = resetInitialQuestions(worksheetDeductionQuestions1223)
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              let numDependentAllowancesTemp; // Line 4
              let adjustmentAllowanceTemp; // Line 5 - Adjustment Allowances
              let worksheetData; // Line 5 - Worksheet
              let additionalWithholdingTemp; // Line 6
              let claimExemptionData; // Line 8 (Exemption from withholding)
              let exemptionStateData; // Line 8 (Exemption from withholding) => Civil Relief Act

              // if exempt from withholding
              if (isExemptFromWithholding) {
                claimExemptionData = claimExemption
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                if (claimExemptionData.exemption_from_withholding === 'civil_relief_act') {
                  exemptionStateData = exemptionSpecificQuestions
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  exemptionStateData = resetInitialQuestions(civilReliefActStateQuestion)
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                }
                // Line 4 (Dependent allowances)
                numDependentAllowancesTemp = 0;

                // Line 5 (Adjustment Allowances + Worksheet)
                adjustmentAllowanceTemp = 0; // Adjustment Allowances
                worksheetData = worksheetResetDeductionData; // Worksheet

                // Line 6 (Additional Withholding Allowances)
                additionalWithholdingTemp = resetInitialQuestions(additionalWithholding) // Line 6
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              } else {
                // Line 4 (Dependent allowances)
                numDependentAllowancesTemp = numDependentAllowances;

                // Line 5 (Adjustment Allowances + Worksheet)
                if (worksheetOpen) {
                  adjustmentAllowanceTemp = adjustmentAllowance;
                  worksheetData = worksheetQuestions
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  adjustmentAllowanceTemp = 0;
                  worksheetData = worksheetResetDeductionData;
                }

                // Additional Withholding Allowances (Line 6)
                additionalWithholdingTemp = additionalWithholding
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                // reset exemption questions
                claimExemptionData = resetInitialQuestions(exemptionInitialQuestions)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                exemptionStateData = resetInitialQuestions(civilReliefActStateQuestion)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              }

              const exemptConditionData = { is_exempt: isExemptFromWithholding };
              const allExemptionsData = { ...exemptConditionData, ...claimExemptionData, ...exemptionStateData }; // Line 8
              const adjustmentAllowanceData = { adjustment_allowance: adjustmentAllowanceTemp, ...worksheetData }; // Line 5 - Adjustment Allowances + Worksheet Data
              const additionalWithholdingData = { ...additionalWithholdingTemp };
              const dependentAllowanceData = { number_of_dependent_allowances: numDependentAllowancesTemp };

              const res = {
                ...basicInfoData,
                ...maritalStatusData,
                ...allExemptionsData,
                ...dependentAllowanceData,
                ...additionalWithholdingData,
                ...adjustmentAllowanceData,
              };

              saveFormData(res, () => {
                setCurrentStep(STEPS.step2);
              });
              scrollTop();
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  // show step 2: review form with filled in information
  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {renderMaritalStatusForm(true)}
        {isExemptFromWithholding && renderExemptionsForm(true)}
        {!isExemptFromWithholding && renderWorksheet(true)}
        {!isExemptFromWithholding && renderadditionalWitholdingForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  // show step 3: hellosign
  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version1223;
